<div class="billing-plans-page account-settings-page">
  <header class="account-settings-page__title-card page-card card">
    <div class="card-body">
      <h1 class="account-settings-page__title bw-title-with-info">
        Billing &amp; plans
        <app-info-tooltip compact placement="right" text="Review your current plan, renewal status, and Paddle-managed subscription from one place."></app-info-tooltip>
      </h1>

      @if (currentUser && billing) {
        <div class="account-settings-page__title-actions">
          <span class="badge text-bg-primary text-uppercase">{{ currentUser.plan.status }}</span>
          <span class="badge text-bg-secondary text-uppercase">{{ billing.environment }}</span>
        </div>
      }
    </div>
  </header>

  @if (isInitialLoading) {
    <section class="page-card card">
      <div class="card-body py-5 text-center">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 mb-0">Loading billing plans...</p>
      </div>
    </section>
  } @else if (hasInitialLoadError) {
    <app-api-load-error
      title="Could not load billing plans"
      message="Some error occurred while loading your subscription and the available plans."
      retryLabel="Retry"
      (retry)="loadPlans()"
    ></app-api-load-error>
  } @else if (currentUser && billing) {
    <div class="d-grid gap-4">
      @if (pageMessage) {
        <div class="alert alert-success mb-0">{{ pageMessage }}</div>
      }
      @if (pageError) {
        <div class="alert alert-danger mb-0">{{ pageError }}</div>
      }

      <section class="page-card card">
        <div class="card-body d-grid gap-3">
          <div class="billing-summary-grid">
            <article class="billing-summary-card">
              <span class="billing-summary-card__label">Current plan</span>
              <h4>{{ currentUser.plan.name }}</h4>
              <p class="mb-0">{{ formatPlanPrice(currentUser.plan) }}</p>
            </article>

            <article class="billing-summary-card">
              <span class="billing-summary-card__label">Billing profile</span>
              @if (billing.customerId) {
                <h4>Connected</h4>
                <p class="mb-0">Subscription status: {{ billing.subscriptionStatus || 'unknown' }}</p>
              } @else {
                <h4>Not connected yet</h4>
                <p class="mb-0">A Paddle billing profile is created after the first paid checkout.</p>
              }
            </article>

            <article class="billing-summary-card">
              <span class="billing-summary-card__label">Next billing date</span>
              @if (billing.nextBilledAt) {
                <h4>{{ billing.nextBilledAt | date: 'mediumDate' }}</h4>
                <p class="mb-0">Subscription ID: {{ billing.subscriptionId || 'pending' }}</p>
              } @else {
                <h4>None scheduled</h4>
                <p class="mb-0">No recurring billing date is tracked yet.</p>
              }
            </article>
          </div>

          @if (billing.scheduledChangeAction) {
            <div class="alert alert-warning mb-0">
              Scheduled change:
              <strong>{{ scheduledChangeLabel() }}</strong>
              @if (billing.scheduledChangeEffectiveAt) {
                around {{ billing.scheduledChangeEffectiveAt | date: 'mediumDate' }}.
              }
            </div>
          }

          <div class="d-flex flex-wrap gap-2">
            @if (billing.portalAvailable) {
              <button
                type="button"
                class="btn btn-outline-secondary"
                [disabled]="isPortalBusy"
                (click)="openBillingPortal()"
              >
                @if (!isPortalBusy) {
                  <span>Manage billing in Paddle</span>
                } @else {
                  <span>Opening...</span>
                }
              </button>
            }
            <a class="btn btn-outline-secondary" routerLink="/account/payment-methods">Billing settings</a>
          </div>
        </div>
      </section>

      <section class="page-card card">
        <div class="card-body d-grid gap-3">
          <div>
            <h3 class="section-title mb-0 bw-title-with-info">
              Choose a plan
              <app-info-tooltip compact placement="right" text="Available self-serve plans and prices are kept in sync with Paddle automatically."></app-info-tooltip>
            </h3>
          </div>

          <div class="billing-plan-grid">
            @for (plan of plans; track plan.code) {
              <article class="billing-plan-card" [class.is-current]="isCurrentPlan(plan)">
                <div class="billing-plan-card__header">
                  <div>
                    <span class="billing-plan-card__eyebrow">{{ plan.code }}</span>
                    <h4>{{ plan.name }}</h4>
                    <p class="mb-0">{{ plan.description || 'No plan description configured yet.' }}</p>
                  </div>

                  @if (isCurrentPlan(plan)) {
                    <span class="badge text-bg-primary">Current plan</span>
                  }
                </div>

                <div class="billing-plan-card__price">{{ formatPlanPrice(plan) }}</div>

                <ul class="billing-plan-card__features">
                  @for (feature of planFeatureList(plan); track feature) {
                    <li>{{ feature }}</li>
                  }
                </ul>

                <div class="billing-plan-card__footer">
                  @if (isCurrentPlan(plan)) {
                    <button type="button" class="btn btn-outline-secondary" disabled>Current plan</button>
                  } @else {
                    <a
                      [class]="actionClass(plan)"
                      [routerLink]="['/billing/checkout']"
                      [queryParams]="{ plan: plan.code }"
                    >
                      {{ actionLabel(plan) }}
                    </a>
                  }
                </div>
              </article>
            }
          </div>
        </div>
      </section>
    </div>
  }
</div>
